import React from 'react';
import { Alert, Box, Skeleton, Stack } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { getDonationReceipt, logger, mapResponseToReceipt, type ReceiptData } from '@kuow/core';
import { Receipt } from '@kuow/shared-ui';

type ReceiptLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: ReceiptData }
  | { status: 'error'; message: string };

export const ReceiptPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const receiptNumber = (searchParams.get('number') ?? '').trim();

  const [receipt, setReceipt] = React.useState<ReceiptLoadState>({ status: 'idle' });

  React.useEffect(() => {
    if (!receiptNumber) {
      setReceipt({ status: 'error', message: 'Missing receipt number' });
      return;
    }

    const controller = new AbortController();
    setReceipt({ status: 'loading' });

    void (async () => {
      try {
        const response = await getDonationReceipt(receiptNumber, { withAuth: false, signal: controller.signal });
        const data = mapResponseToReceipt(response);
        setReceipt({ status: 'success', data });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        logger.error('Failed to load receipt for ReceiptPage', { receiptNumber, error });
        const message = error instanceof Error ? error.message : 'Failed to load receipt';
        setReceipt({ status: 'error', message });
      }
    })();

    return () => controller.abort();
  }, [receiptNumber]);

  const content = (() => {
    if (receipt.status === 'idle' || receipt.status === 'loading') {
      return (
        <Box w="full" maxW="480px">
          <Stack gap={4}>
            <Skeleton height="24px" borderRadius="md" />
            <Skeleton height="420px" borderRadius="lg" />
          </Stack>
        </Box>
      );
    }

    if (receipt.status === 'error') {
      return (
        <Box w="full" maxW="480px">
          <Alert.Root status="error" variant="outline">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Error loading receipt</Alert.Title>
              <Alert.Description>{receipt.message}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        </Box>
      );
    }

    return (
      <Box w="full" maxW="480px" bg="bg" borderRadius="l3" px={8} py={4}>
        <Receipt receiptData={receipt.data} mode="standalone" />
      </Box>
    );
  })();

  return (
    <Box w="full" flex="1" display="flex" alignItems="center" justifyContent="center" py={8} px={4}>
      {content}
    </Box>
  );
};


